/**
 * MarketFx — the presentation state machine for market conditions.
 *
 * Reads the chaos ledger once per frame (through `marketReadout` and the
 * floor rule in `pit-visual.js`), detects the EDGES — a kind arriving, a kind
 * running out — and drives every cue's transition on its own clock:
 *
 *   floor    pops in as a WIPE from the arena centre to the rim (a shock
 *            front, not a cross-fade), and dies by NOISE EROSION with a
 *            bright front, the pass-2 rule for everything: nothing fades in,
 *            things pop in and erode out.
 *   fog      rolls in from one side of the arena over ~0.8 s and erodes out.
 *   shift    a diagonal grade wipe across the screen, in and back out.
 *   tremor   a jolt on arrival, then a continuous rumble scaled by magnitude.
 *   gravity  the ambient motes drift up while it is low.
 *
 * Pure: no three.js, no DOM. Every cue is a SINK the caller wires up, so the
 * whole thing runs under `node --test` with spies (`market-fx.test.js`).
 * Time is the caller's delta in seconds, so a hitstop or a frozen capture
 * clock freezes the transitions with everything else.
 */

import { marketReadout, allEntries, isLive, isMarketEnded } from './market-state.js';
import { mutationVisualState } from './pit-visual.js';

/** The floor wipe, centre to rim. Fast: an event, not a fade. */
export const FLOOR_SWEEP_MS = 380;
/** The floor's noise erosion on removal. */
export const FLOOR_ERODE_MS = 520;
/** The fog front crossing the arena. */
export const FOG_ROLL_MS = 800;
/** The fog dissolving on removal. */
export const FOG_ERODE_MS = 700;
/** The grade wipe, in and out. */
export const SHIFT_WIPE_MS = 320;

const easeOut = (t) => 1 - (1 - t) * (1 - t);
const clamp01 = (t) => (t < 0 ? 0 : t > 1 ? 1 : t);

export class MarketFx {
  /**
   * @param {object} sinks
   * @param {(kind: string, mutation: object) => void} [sinks.onActivate]
   * @param {(kind: string) => void} [sinks.onExpire]
   * @param {(state: {density:number, roll:number, erode:number}) => void} [sinks.fog]
   * @param {(hueDeg: number|null, wipe: number) => void} [sinks.shift]
   * @param {(magnitude: number) => void} [sinks.tremor]
   * @param {(magnitude: number) => void} [sinks.jolt]
   * @param {(multiplier: number) => void} [sinks.gravity]
   */
  constructor(sinks = {}) {
    this.sinks = sinks;
    this._readout = {};
    this._entries = [];
    this.reset();
  }

  /**
   * A new match, or an arena rebuild. Every sink that was last told something
   * non-neutral is told the neutral value NOW. `update()` only speaks on a
   * change, and a reset that zeroed its own memory silently left the camera
   * rumbling, dusting and jolting — and the motes floating — through the
   * whole next match, because `setTremor(0)` and `setAmbientGravity(1)` were
   * never said (`market-fx.test.js`, "a new world neutralises every sink").
   */
  reset() {
    const hadTremor = this._tremor > 0;
    const hadGravity = this._gravity < 1;
    const hadFog = !!this.fog && this.fog.phase !== 'off';
    const hadShift = !!this.shift && this.shift.phase !== 'off';
    const shiftHue = this.shift?.hue ?? null;
    this._world = null;
    this._live = new Set();
    this._nextLive = new Set();
    this.floor = { kind: null, raised: null, quads: false, sweep: 0, erode: 0, visible: false, phase: 'off', mutation: null };
    this.fog = { phase: 'off', k: 0, target: 0, density: 0, roll: 0, erode: 0 };
    this.shift = { phase: 'off', k: 0, hue: null, wipe: 0 };
    this._tremor = 0;
    this._gravity = 1;
    this._fogWasOn = false;
    this._shiftWasOn = false;
    if (hadTremor) this.sinks.tremor?.(0);
    if (hadGravity) this.sinks.gravity?.(1);
    if (hadFog) this.sinks.fog?.(this.fog);
    if (hadShift) this.sinks.shift?.(shiftHue, 0);
  }

  /**
   * @param {number} delta seconds since the last frame (0 during hitstop)
   * @param {object|null} world the predictor world (or the arena tool's stand-in)
   */
  update(delta, world) {
    if (world !== this._world) {
      // A new match, a new predictor: nothing from the old ledger carries
      // over, and every sink hears the neutral value (see `reset`).
      this.reset();
      this._world = world;
    }
    const r = marketReadout(world, this._readout);
    const dtMs = Math.max(0, delta) * 1000;

    // ── Edges ─────────────────────────────────────────────────────
    const next = this._nextLive;
    next.clear();
    const entries = allEntries(world, this._entries);
    for (const e of entries) if (isLive(e, world.tick)) next.add(e.kind);
    for (const kind of next) {
      if (this._live.has(kind)) continue;
      const mutation = latest(entries, world.tick, kind);
      if (kind === 'tremor') this.sinks.jolt?.(r.tremor);
      this.sinks.onActivate?.(kind, mutation);
    }
    for (const kind of this._live) {
      if (!next.has(kind)) this.sinks.onExpire?.(kind);
    }
    this._live.clear();
    for (const kind of next) this._live.add(kind);

    // ── Floor ─────────────────────────────────────────────────────
    this._updateFloor(world, dtMs);

    // ── Fog ───────────────────────────────────────────────────────
    this._updateFog(r.fog, dtMs);

    // ── Grade ─────────────────────────────────────────────────────
    this._updateShift(r.hue, dtMs);

    // ── Tremor, gravity ───────────────────────────────────────────
    if (r.tremor !== this._tremor) {
      this._tremor = r.tremor;
      this.sinks.tremor?.(r.tremor);
    }
    if (r.gravity !== this._gravity) {
      this._gravity = r.gravity;
      this.sinks.gravity?.(r.gravity);
    }
    return this;
  }

  _updateFloor(world, dtMs) {
    const f = this.floor;
    // An ended world (the results screen, a frozen ledger) has no floor.
    const { floor: floorMut, raised, ground } = mutationVisualState(isMarketEnded(world) ? null : world);
    f.mutation = floorMut;
    if (ground) {
      if (f.phase !== 'on' || f.kind !== ground) {
        // Arrival, or one floor replacing another: a fresh wipe under the new
        // look. A re-arrival mid-erosion restarts clean.
        f.kind = ground;
        f.phase = 'on';
        f.sweep = 0;
        f.erode = 0;
        f.visible = true;
      }
      // The raised quads follow the live ice/conveyor while the ground is on
      // (ice can run out under a lava that is still burning); the latch only
      // freezes during the fade-out, so a lava-only end never flashes stale
      // quads while the ground erodes.
      f.raised = raised;
      f.quads = raised !== null;
      f.sweep = clamp01(f.sweep + dtMs / FLOOR_SWEEP_MS);
    } else if (f.phase === 'on') {
      f.phase = 'out';
      f.sweep = 1;
      f.erode = 0;
    }
    if (f.phase === 'out') {
      f.erode = clamp01(f.erode + dtMs / FLOOR_ERODE_MS);
      if (f.erode >= 1) {
        f.phase = 'off';
        f.kind = null;
        f.raised = null;
        f.quads = false;
        f.visible = false;
      }
    }
  }

  _updateFog(level, dtMs) {
    const g = this.fog;
    if (level > 0) {
      g.target = level;
      if (g.phase === 'off' || g.phase === 'out') {
        // Rolling back in while still dissolving: the front comes back
        // across from the start, which reads as a second roll, not a glitch.
        g.phase = 'in';
        g.k = 0;
        g.erode = 0;
      }
      if (g.phase === 'in') {
        g.k = clamp01(g.k + dtMs / FOG_ROLL_MS);
        if (g.k >= 1) g.phase = 'on';
      }
      g.roll = g.phase === 'on' ? 1 : g.k;
      g.density = g.target * (g.phase === 'on' ? 1 : easeOut(g.k));
    } else if (g.phase === 'in' || g.phase === 'on') {
      g.phase = 'out';
      g.k = 0;
      g.roll = 1;
    }
    if (g.phase === 'out') {
      g.k = clamp01(g.k + dtMs / FOG_ERODE_MS);
      g.erode = g.k;
      g.density = g.target * (1 - g.k * 0.6);
      if (g.k >= 1) {
        g.phase = 'off';
        g.density = 0;
        g.roll = 0;
        g.erode = 0;
      }
    }
    if (g.phase !== 'off' || this._fogWasOn) this.sinks.fog?.(g);
    this._fogWasOn = g.phase !== 'off';
  }

  _updateShift(hue, dtMs) {
    const s = this.shift;
    if (hue !== null) {
      const was = s.phase;
      if (was === 'off' || was === 'out') {
        // Coming back mid-wipe-out continues from where the front is.
        s.phase = 'in';
        s.k = was === 'out' ? s.k : 0;
      } else if (was === 'on' && s.hue !== hue) {
        // A new hue mid-shift wipes across again in the new look.
        s.phase = 'in';
        s.k = 0;
      }
      s.hue = hue;
      if (s.phase === 'in') {
        s.k = clamp01(s.k + dtMs / SHIFT_WIPE_MS);
        if (s.k >= 1) s.phase = 'on';
      }
      s.wipe = s.phase === 'on' ? 1 : s.k;
    } else if (s.phase === 'in' || s.phase === 'on') {
      s.phase = 'out';
      s.k = 1;
    }
    if (s.phase === 'out') {
      s.k = clamp01(s.k - dtMs / SHIFT_WIPE_MS);
      s.wipe = s.k;
      if (s.k <= 0) s.phase = 'off';
    }
    if (s.phase !== 'off' || this._shiftWasOn) this.sinks.shift?.(s.hue, s.wipe);
    this._shiftWasOn = s.phase !== 'off';
  }
}

/** The most recently applied live entry of a kind — the one the sim listens to. */
function latest(entries, tick, kind) {
  let found = null;
  for (const e of entries) if (e.kind === kind && isLive(e, tick)) found = e.mutation;
  return found;
}
